<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>

<app-refresh-challenge-content
  *ngIf="!isCreateMode && challenge"
  [challengeId]="id"
  [disabled]="!challenge?.canRefreshContent || saving || composing || loading || hasUnsavedRefreshEdits"
  [disabledReason]="challenge?.canRefreshContent ? '' : (challenge?.refreshContentUnavailableReason || 'Refresh availability was not supplied by the server. Ensure the updated backend is deployed.')"
  (applied)="reload()">
</app-refresh-challenge-content>

<mat-card class="block" *ngIf="isCreateMode || challenge">
  <div class="header-row">
    <div>
      <h2>{{ challengeLabel }}</h2>
      <p class="meta" *ngIf="!isCreateMode">{{ challenge?.status }} · {{ challenge?.type }} · {{ challenge?.slug }}</p>
      <p class="meta" *ngIf="isCreateMode">Create a draft and continue editing in the same screen.</p>
    </div>
    <div class="actions" *ngIf="!isCreateMode">
      <button mat-stroked-button type="button" (click)="copyChallenge()" [disabled]="saving">Copy</button>
      <button mat-stroked-button type="button" (click)="publish()" [disabled]="saving">{{ publishActionLabel }}</button>
      <button mat-stroked-button type="button" (click)="archive()" [disabled]="saving">{{ archiveActionLabel }}</button>
      <button
        *ngIf="challenge?.status === 'draft'"
        mat-stroked-button
        color="warn"
        type="button"
        (click)="deleteDraft()"
        [disabled]="saving"
      >
        Delete
      </button>
    </div>
  </div>

  <div class="publish-checklist" *ngIf="publishChecklist.length">
    <p class="meta"><strong>Before {{ isFirstSgaEvaluationMode() ? 'activate' : 'publish' }}, still needed:</strong></p>
    <ul>
      <li *ngFor="let item of publishChecklist">{{ item }}</li>
    </ul>
  </div>
  <p class="meta" *ngIf="!publishChecklist.length">{{ isFirstSgaEvaluationMode() ? 'Activate' : 'Publish' }} checklist looks complete.</p>

  <form class="form-grid" [formGroup]="form" (ngSubmit)="save()">
    <h3 class="full">Audience</h3>
    <mat-form-field appearance="outline">
      <mat-label>Role</mat-label>
      <mat-select formControlName="careerRoleSlug" (selectionChange)="onRoleChanged()">
        <mat-option *ngFor="let role of roles" [value]="role.roleSlug">{{ role.roleName }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Profile</mat-label>
      <mat-select
        formControlName="seniorityLevel"
        (selectionChange)="onProfileChanged()"
      >
        <mat-option *ngFor="let profile of profiles" [value]="profile">{{ profile }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" *ngIf="!isFirstSgaEvaluationMode()">
      <mat-label>Specialization</mat-label>
      <mat-select
        formControlName="specializationSlug"
        (selectionChange)="onSpecializationChanged()"
      >
        <mat-option value="">Core only</mat-option>
        <mat-option *ngFor="let spec of specializations" [value]="spec.slug">{{ spec.name }}</mat-option>
      </mat-select>
    </mat-form-field>
    <p class="full meta" *ngIf="isFirstSgaEvaluationMode()">
      First SGA evaluation matches role and profile only (core skills). Specialization is not used.
    </p>

    <p class="full meta" *ngIf="roleInactiveHint">{{ roleInactiveHint }}</p>

    <h3 class="full">Challenge setup</h3>
    <mat-form-field appearance="outline">
      <mat-label>Name</mat-label>
      <input matInput formControlName="title" />
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Slug</mat-label>
      <input matInput formControlName="slug" />
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Challenge type</mat-label>
      <mat-select formControlName="type" (selectionChange)="onTypeSelected()">
        <mat-option *ngFor="let type of types" [value]="type">{{ type }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-checkbox formControlName="showOnWebsite">Show on website</mat-checkbox>
    <p class="full meta type-help" *ngIf="selectedTypeHelp">{{ selectedTypeHelp }}</p>

    <mat-form-field appearance="outline">
      <mat-label>Intellectual property</mat-label>
      <mat-select formControlName="ipPolicy">
        <mat-option *ngFor="let policy of ipPolicies" [value]="policy">{{ ipPolicyLabels[policy] }}</mat-option>
      </mat-select>
    </mat-form-field>
    <p class="full meta" *ngIf="selectedIpPolicyHelp">{{ selectedIpPolicyHelp }}</p>

    <h3 class="full">Access</h3>
    <mat-form-field appearance="outline">
      <mat-label>Access mode</mat-label>
      <mat-select formControlName="accessMode" (selectionChange)="onAccessModeChanged()">
        <mat-option value="open">Open challenge (core skills)</mat-option>
        <mat-option value="first_sga_evaluation">First SGA evaluation</mat-option>
        <mat-option value="personalized_progression">Personalized progression (learner pack)</mat-option>
        <mat-option value="prerequisites">Prerequisites required</mat-option>
      </mat-select>
    </mat-form-field>

    <p class="full meta banner" *ngIf="isFirstSgaEvaluationMode()">
      First SGA evaluation — core skills only, no dates, activate or deactivate to offer it. AI scoring, no sponsors, judges, or placement prizes.
    </p>

    <p class="full meta banner" *ngIf="isPersonalizedProgressionMode()">
      Personalized progression — learner-private pack challenge. Not used for first-SGA acquisition or public catalogs.
    </p>

    <mat-form-field appearance="outline" class="full" *ngIf="form.controls.accessMode.value === 'prerequisites'">
      <mat-label>Prerequisite skills</mat-label>
      <mat-select
        multiple
        [(ngModel)]="selectedPrerequisiteSkillIds"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onPrerequisiteSkillsChanged($event)"
      >
      @for (skill of prerequisiteSkillOptions;  track trackBySkillId(skill)) {
        <mat-option [value]="skill.skillId">
          {{ skill.skillName }}
        </mat-option>
      }
      </mat-select>
    </mat-form-field>
    <p class="full meta" *ngIf="form.controls.accessMode.value === 'prerequisites'">
      Each prerequisite skill id is treated as all role-graph topics for that skill meeting the selected role/profile/specialization proficiency.
    </p>

    <h3 class="full">Content</h3>
    <app-challenge-cover-field
      *ngIf="!isCreateMode"
      class="full"
      [challengeId]="id"
      [displayUrl]="coverDisplayUrl"
      [canGenerate]="canGenerateCover"
      (generated)="onCoverGenerated($event)"
    ></app-challenge-cover-field>
    <mat-form-field appearance="outline" class="full">
      <mat-label>Prompt (admin only)</mat-label>
      <textarea matInput rows="4" formControlName="composePrompt"></textarea>
      <mat-hint>
        Describe the problem idea, constraints, and domain. Not shown to learners. Used to generate title, summary, description, FAQ, and skills.
      </mat-hint>
    </mat-form-field>

    <div class="full actions">
      <button mat-flat-button color="primary" type="button" (click)="composeChallenge()" [disabled]="!canCompose">
        {{ composing ? 'Generating…' : 'Generate title, summary, description + skills' }}
      </button>
      <button mat-stroked-button type="button" (click)="suggestSkills()" [disabled]="!canSuggestSkills">
        {{ suggesting ? 'Suggesting…' : 'Refresh skills from description' }}
      </button>
    </div>

    <mat-form-field appearance="outline" class="full">
      <mat-label>Summary</mat-label>
      <textarea matInput rows="3" formControlName="summary"></textarea>
      <mat-hint>Learner-facing blurb. Filled by Generate; edit before you save.</mat-hint>
    </mat-form-field>

    <!-- <mat-form-field appearance="outline" class="full">
      <mat-label>Description (markdown)</mat-label>
      <textarea matInput rows="10" formControlName="description" (input)="onDescriptionChanged()"></textarea>
      <mat-hint>
        Use Problem definition, Task brief, Language and stack, Scenario, Deliverables, Success criteria, How to submit, and Intellectual property. Do not put Skills covered or FAQ in the description (those are separate sections). How to submit must say: connect GitHub before start (every teammate); SahkaarX creates the private repo, invites teammates, and accepts those invites; clone and push that repo; the team leader submits. Intellectual property must match the selected IP option.
      </mat-hint>
    </mat-form-field> -->
    <div class="full md-editor-container">
      <label class="block text-sm font-semibold text-slate-700 mb-1">Description (markdown brief)</label>
      <textarea #editor ></textarea>
    </div>

    <h3 class="full">FAQ</h3>
    <p class="full meta">Generated with the description. Edit before you save. Empty rows are dropped.</p>
    <div class="full faq-list">
      <div class="faq-row" *ngFor="let item of faqItems; let i = index">
        <mat-form-field appearance="outline">
          <mat-label>Question</mat-label>
          <input matInput [(ngModel)]="item.question" [ngModelOptions]="{ standalone: true }" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Answer</mat-label>
          <textarea matInput rows="3" [(ngModel)]="item.answer" [ngModelOptions]="{ standalone: true }"></textarea>
        </mat-form-field>
        <button mat-stroked-button type="button" (click)="removeFaqItem(i)">Remove</button>
      </div>
      <button mat-stroked-button type="button" (click)="addFaqItem()">Add FAQ item</button>
    </div>

    <h3 class="full">Skills</h3>
    <mat-form-field appearance="outline" class="full">
      <mat-label>Challenge skills and topics</mat-label>
      <mat-select
        multiple
        [(ngModel)]="selectedSkillKeys"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="onSkillsChanged($event)"
      >
      @for (facet of catalog; track trackByFacet(facet)) {
        <mat-option [value]="facetKey(facet)" [disabled]="isSkillOptionDisabled(facet)">
          {{ facetLabel(facet) }}
          <span *ngIf="isLocked(facet)"> · fundamental</span>
          <span *ngIf="facetTargetLabel(facet)"> · {{ facetTargetLabel(facet) }}</span>
        </mat-option>
      }
      </mat-select>
    </mat-form-field>
    <p class="full meta" *ngIf="catalogLoading">Loading catalog…</p>
    <p class="full meta" *ngIf="isCoreSkillsOnlyMode()">
      Open and first-eval challenges evaluate all core skills for the role. Non-core facets cannot be added.
    </p>
    <p class="full meta" *ngIf="coveragePreview as cov">
      This challenge covers {{ selectedSkillKeys.length }} of {{ cov.catalogFacetCount }} role facets ({{ cov.coveragePercent }}%).
    </p>
    <p class="full meta stale" *ngIf="skillsStale">Audience or content changed since the last generation. Re-run generation or refine the skill selection.</p>

    <h3 class="full">Rubrics</h3>
    <div class="full rubric-weights">
      <div class="rubric-weight" *ngFor="let key of rubricCriteria">
        <label>
          {{ rubricLabels[key] || key }} weight
          <input
            type="number"
            min="0.1"
            step="0.1"
            [ngModel]="rubricWeights[key]"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="setRubricWeight(key, $event)"
          />
        </label>
        <p class="meta">{{ (rubricBands[key] || []).join(' · ') }}</p>
      </div>
    </div>

    <ng-container *ngIf="!isFirstSgaEvaluationMode()">
    <h3 class="full">Key dates</h3>
    <p class="full meta" *ngIf="schedulePhaseHelp">{{ schedulePhaseHelp }}</p>
    <div class="full key-date-field" [formGroup]="keyDates">
      <mat-form-field appearance="outline">
        <mat-label>Registration opens</mat-label>
        <input
          matInput
          [matDatepicker]="registrationOpensPicker"
          [value]="keyDates.controls.registrationOpensAt.value"
          [disabled]="isKeyDateLocked('registrationOpensAt')"
          (dateChange)="onKeyDatePicked('registrationOpensAt', $event.value)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="registrationOpensPicker"></mat-datepicker-toggle>
        <mat-datepicker #registrationOpensPicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="time-field">
        <mat-label>Time</mat-label>
        <input
          matInput
          type="time"
          [value]="keyDateTime('registrationOpensAt')"
          [disabled]="isKeyDateLocked('registrationOpensAt')"
          (change)="onKeyTimeChanged('registrationOpensAt', $any($event.target).value)"
        />
      </mat-form-field>
    </div>

    <div class="full key-date-field" [formGroup]="keyDates">
      <mat-form-field appearance="outline">
        <mat-label>Registration closes</mat-label>
        <input
          matInput
          [matDatepicker]="registrationClosesPicker"
          [value]="keyDates.controls.registrationClosesAt.value"
          [disabled]="isKeyDateLocked('registrationClosesAt')"
          (dateChange)="onKeyDatePicked('registrationClosesAt', $event.value)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="registrationClosesPicker"></mat-datepicker-toggle>
        <mat-datepicker #registrationClosesPicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="time-field">
        <mat-label>Time</mat-label>
        <input
          matInput
          type="time"
          [value]="keyDateTime('registrationClosesAt')"
          [disabled]="isKeyDateLocked('registrationClosesAt')"
          (change)="onKeyTimeChanged('registrationClosesAt', $any($event.target).value)"
        />
      </mat-form-field>
    </div>

    <div class="full key-date-field" [formGroup]="keyDates">
      <mat-form-field appearance="outline">
        <mat-label>Starts</mat-label>
        <input
          matInput
          [matDatepicker]="startsAtPicker"
          [value]="keyDates.controls.startsAt.value"
          [disabled]="isKeyDateLocked('startsAt')"
          (dateChange)="onKeyDatePicked('startsAt', $event.value)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="startsAtPicker"></mat-datepicker-toggle>
        <mat-datepicker #startsAtPicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="time-field">
        <mat-label>Time</mat-label>
        <input
          matInput
          type="time"
          [value]="keyDateTime('startsAt')"
          [disabled]="isKeyDateLocked('startsAt')"
          (change)="onKeyTimeChanged('startsAt', $any($event.target).value)"
        />
      </mat-form-field>
    </div>

    <div class="full key-date-field" [formGroup]="keyDates">
      <mat-form-field appearance="outline">
        <mat-label>Ends</mat-label>
        <input
          matInput
          [matDatepicker]="endsAtPicker"
          [value]="keyDates.controls.endsAt.value"
          [disabled]="isKeyDateLocked('endsAt')"
          (dateChange)="onKeyDatePicked('endsAt', $event.value)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="endsAtPicker"></mat-datepicker-toggle>
        <mat-datepicker #endsAtPicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="time-field">
        <mat-label>Time</mat-label>
        <input
          matInput
          type="time"
          [value]="keyDateTime('endsAt')"
          [disabled]="isKeyDateLocked('endsAt')"
          (change)="onKeyTimeChanged('endsAt', $any($event.target).value)"
        />
      </mat-form-field>
    </div>

    <div class="full key-date-field" [formGroup]="keyDates">
      <mat-form-field appearance="outline">
        <mat-label>Judging ends</mat-label>
        <input
          matInput
          [matDatepicker]="judgingEndsPicker"
          [value]="keyDates.controls.judgingEndsAt.value"
          [disabled]="isKeyDateLocked('judgingEndsAt')"
          (dateChange)="onKeyDatePicked('judgingEndsAt', $event.value)"
        />
        <mat-datepicker-toggle matIconSuffix [for]="judgingEndsPicker"></mat-datepicker-toggle>
        <mat-datepicker #judgingEndsPicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" class="time-field">
        <mat-label>Time</mat-label>
        <input
          matInput
          type="time"
          [value]="keyDateTime('judgingEndsAt')"
          [disabled]="isKeyDateLocked('judgingEndsAt')"
          (change)="onKeyTimeChanged('judgingEndsAt', $any($event.target).value)"
        />
      </mat-form-field>
    </div>
    </ng-container>

    <h3 class="full">Participation policy</h3>

    <div class="full participation-mode-group">
      <p class="field-label">Participation mode</p>
      <mat-radio-group formControlName="participationMode" class="participation-radio-group">
        <mat-radio-button *ngFor="let opt of participationModeOptions" [value]="opt.value" class="participation-radio-btn">
          <span class="radio-label">{{ opt.label }}</span>
          <span class="radio-hint">{{ opt.hint }}</span>
        </mat-radio-button>
      </mat-radio-group>
      <p class="meta" *ngIf="form.controls.participationMode.value === 'dual' || form.controls.participationMode.value === 'pair_only'">
        Pair problem statements with per-seat briefs and skill mappings are configured in the <strong>Problem Statements</strong> tab after saving.
      </p>
    </div>

    <mat-checkbox class="full" formControlName="requireCommitContribution">Require commit contribution (each member must commit)</mat-checkbox>
    <mat-checkbox class="full" formControlName="allowLateRegistrationDuringActive">Allow registration during coding time</mat-checkbox>

    <mat-form-field appearance="outline">
      <mat-label>Min commits per member</mat-label>
      <input matInput type="number" formControlName="minCommitsPerMember" />
    </mat-form-field>

    <button mat-flat-button color="primary" type="submit" [disabled]="saving">
      {{ isCreateMode ? 'Create challenge draft' : 'Save challenge' }}
    </button>
  </form>
</mat-card>

<p *ngIf="!loading && !isCreateMode && !challenge" class="empty">Challenge not found.</p>
